import { useState, useEffect, useCallback } from "react";
import { httpClient, getPartnerAwareApiUrl } from "@/providers/dataProvider/dataProvider";
import type { TaskTypeRegistryEntry, TaskTypesConfig } from "@/types";

// ==================== TYPES ====================

interface UseTaskTypeConfigOptions {
  partnerId: string;
  enabled?: boolean;
}

interface UseTaskTypeConfigReturn {
  /** All registered task types from GET /task-types */
  registry: TaskTypeRegistryEntry[];
  /** Partner's current task type config from GET /partners/{id}/configuration/task-types */
  config: TaskTypesConfig;
  isLoadingRegistry: boolean;
  isLoading: boolean;
  isUpdating: boolean;
  error: Error | null;
  registryError: Error | null;
  updateError: Error | null;
  /** PUT full replacement to /partners/{id}/configuration/task-types */
  saveConfig: (taskTypes: TaskTypesConfig) => Promise<void>;
  refetch: () => Promise<void>;
}

// ==================== HOOK ====================

export const useTaskTypeConfig = ({ partnerId, enabled = true }: UseTaskTypeConfigOptions): UseTaskTypeConfigReturn => {
  const [registry, setRegistry] = useState<TaskTypeRegistryEntry[]>([]);
  const [config, setConfig] = useState<TaskTypesConfig>({});
  const [isLoadingRegistry, setIsLoadingRegistry] = useState(enabled);
  const [isLoading, setIsLoading] = useState(enabled);
  const [isUpdating, setIsUpdating] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [registryError, setRegistryError] = useState<Error | null>(null);
  const [updateError, setUpdateError] = useState<Error | null>(null);

  // Fetch task type registry
  const fetchRegistry = useCallback(async () => {
    setIsLoadingRegistry(true);
    setRegistryError(null);

    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/task-types`);
      const items = Array.isArray(json) ? json : (json?.data ?? []);
      setRegistry(items);
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch task type registry");
      setRegistryError(error);
      console.error("[useTaskTypeConfig] Registry fetch error:", error);
    } finally {
      setIsLoadingRegistry(false);
    }
  }, []);

  // Fetch partner task type config
  const fetchConfig = useCallback(async () => {
    if (!partnerId || !enabled) return;

    setIsLoading(true);
    setError(null);

    try {
      const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/${partnerId}/configuration/task-types`);
      setConfig(json?.task_types ?? {});
    } catch (err) {
      const error = err instanceof Error ? err : new Error("Failed to fetch task type config");
      setError(error);
      console.error("[useTaskTypeConfig] Config fetch error:", error);
    } finally {
      setIsLoading(false);
    }
  }, [partnerId, enabled]);

  // Save (PUT full replace) partner task type config
  const saveConfig = useCallback(
    async (taskTypes: TaskTypesConfig) => {
      if (!partnerId) return;

      setIsUpdating(true);
      setUpdateError(null);

      try {
        const { json } = await httpClient(`${getPartnerAwareApiUrl()}/partners/${partnerId}/configuration/task-types`, {
          method: "PUT",
          body: JSON.stringify({ task_types: taskTypes }),
        });
        setConfig(json?.task_types ?? taskTypes);
      } catch (err) {
        const error = err instanceof Error ? err : new Error("Failed to save task type config");
        setUpdateError(error);
        console.error("[useTaskTypeConfig] Save error:", error);
        throw error;
      } finally {
        setIsUpdating(false);
      }
    },
    [partnerId]
  );

  // Initial fetch — registry and config in parallel
  useEffect(() => {
    void fetchRegistry();
  }, [fetchRegistry]);

  useEffect(() => {
    void fetchConfig();
  }, [fetchConfig]);

  return {
    registry,
    config,
    isLoadingRegistry,
    isLoading,
    isUpdating,
    error,
    registryError,
    updateError,
    saveConfig,
    refetch: fetchConfig,
  };
};
